import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardDocumentListIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { SelectInput, TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  ansprechpartnerDisplayName,
  createGpBestellnummerRegel,
  deleteGpBestellnummerRegel,
  listGeschaeftspartnerAnsprechpartner,
  listGeschaeftspartnerObjektadressen,
  listGpBestellnummerRegeln,
  updateGpBestellnummerRegel,
  type GpBestellnummerRegel,
  type KontaktAnsprechpartner,
  type Objektadresse,
} from "@/lib/kontakte";
import { useRealtimeSync } from "@/lib/useRealtime";
import { ROW_ACTIONS_HEADER_CLASS, TableRowActions } from "@/pages/kontakte/TableRowActions";

interface RegelForm {
  bestellnummer: string;
  ansprechpartnerId: string;
  heroAddressId: string;
}

const EMPTY_FORM: RegelForm = {
  bestellnummer: "",
  ansprechpartnerId: "",
  heroAddressId: "",
};

function formatRegelBedingung(regel: GpBestellnummerRegel): string {
  const parts: string[] = [];
  if (regel.ansprechpartner_id != null) {
    parts.push(`Ansprechpartner: ${regel.ansprechpartner_name || `#${regel.ansprechpartner_id}`}`);
  }
  if (regel.hero_address_id != null) {
    parts.push(`Ort: ${regel.ort_bezeichnung || `#${regel.hero_address_id}`}`);
  }
  return parts.join(" · ") || "—";
}

function toForm(regel: GpBestellnummerRegel): RegelForm {
  return {
    bestellnummer: regel.bestellnummer,
    ansprechpartnerId: regel.ansprechpartner_id != null ? String(regel.ansprechpartner_id) : "",
    heroAddressId: regel.hero_address_id != null ? String(regel.hero_address_id) : "",
  };
}

function formToPayload(form: RegelForm) {
  const ansprechpartner_id = form.ansprechpartnerId ? Number(form.ansprechpartnerId) : null;
  const hero_address_id = form.heroAddressId ? Number(form.heroAddressId) : null;
  return {
    bestellnummer: form.bestellnummer.trim(),
    ansprechpartner_id,
    hero_address_id,
  };
}

interface KundeBestellnummerRegelnSectionProps {
  geschaeftspartnerId: number;
  heroContactId?: number | null;
}

export function KundeBestellnummerRegelnSection({
  geschaeftspartnerId,
}: KundeBestellnummerRegelnSectionProps) {
  const [rows, setRows] = useState<GpBestellnummerRegel[]>([]);
  const [ansprechpartner, setAnsprechpartner] = useState<KontaktAnsprechpartner[]>([]);
  const [objektadressen, setObjektadressen] = useState<Objektadresse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<RegelForm>(EMPTY_FORM);
  const [formBaseline, setFormBaseline] = useState<RegelForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [regeln, aps, addresses] = await Promise.all([
        listGpBestellnummerRegeln(geschaeftspartnerId),
        listGeschaeftspartnerAnsprechpartner(geschaeftspartnerId),
        listGeschaeftspartnerObjektadressen(geschaeftspartnerId).catch(() => []),
      ]);
      setRows(regeln);
      setAnsprechpartner(aps);
      setObjektadressen(addresses);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Bestellnummer-Regeln konnten nicht geladen werden.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [geschaeftspartnerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["kontakte"],
    paused: formOpen,
    enabled: Number.isFinite(geschaeftspartnerId),
    onChange: () => void reload({ silent: true }),
  });

  function openCreate() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormBaseline(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(regel: GpBestellnummerRegel) {
    const initial = toForm(regel);
    setEditId(regel.id);
    setForm(initial);
    setFormBaseline(initial);
    setFormError(null);
    setFormOpen(true);
  }

  async function handleSave() {
    const payload = formToPayload(form);
    if (!payload.bestellnummer) {
      setFormError("Bitte eine Bestellnummer eingeben.");
      return;
    }
    if (payload.ansprechpartner_id == null && payload.hero_address_id == null) {
      setFormError("Bitte mindestens Ansprechpartner oder Objektadresse auswählen.");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (editId) {
        await updateGpBestellnummerRegel(geschaeftspartnerId, editId, payload);
      } else {
        await createGpBestellnummerRegel(geschaeftspartnerId, payload);
      }
      setFormOpen(false);
      await reload({ silent: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Regel konnte nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(regel: GpBestellnummerRegel) {
    if (!window.confirm(`Regel „${regel.bestellnummer}" wirklich löschen?`)) return;
    try {
      await deleteGpBestellnummerRegel(geschaeftspartnerId, regel.id);
      await reload({ silent: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Regel konnte nicht gelöscht werden.",
      );
    }
  }

  const columns = useMemo(
    () => [
      {
        key: "bestellnummer",
        header: "Bestellnummer",
        sortable: true,
        sortValue: (regel: GpBestellnummerRegel) => regel.bestellnummer,
        render: (regel: GpBestellnummerRegel) => (
          <span className="font-mono text-sm">{regel.bestellnummer}</span>
        ),
      },
      {
        key: "bedingung",
        header: "Gilt für",
        sortable: true,
        sortValue: (regel: GpBestellnummerRegel) => formatRegelBedingung(regel),
        render: (regel: GpBestellnummerRegel) => formatRegelBedingung(regel),
      },
      {
        key: "actions",
        header: "",
        headerClassName: ROW_ACTIONS_HEADER_CLASS.two,
        render: (regel: GpBestellnummerRegel) => (
          <TableRowActions
            onEdit={() => openEdit(regel)}
            onDelete={() => void handleDelete(regel)}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Card
      title="Bestellnummern"
      description="Kunden-Bestellnummern, die je nach Ansprechpartner, Objektadresse oder Kombination aus beiden automatisch auf Belege übernommen werden."
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={openCreate}
          leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
        >
          Regel hinzufügen
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="Fehler" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardDocumentListIcon />}
          title="Noch keine Bestellnummer-Regeln"
          description="Ohne Regel wird die Bestellnummer je Beleg manuell eingetragen. Regeln sparen diesen Schritt bei wiederkehrenden Aufträgen."
          action={
            <Button size="sm" onClick={openCreate} leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}>
              Regel hinzufügen
            </Button>
          }
        />
      ) : (
        <Table
          columns={columns}
          rows={rows}
          rowKey={(regel) => regel.id}
          rowClassName={() => "align-middle"}
          stickyEndColumnCount={1}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? "Bestellnummer-Regel bearbeiten" : "Bestellnummer-Regel hinzufügen"}
        dirty={hasFormChanges(form, formBaseline)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </>
        }
      >
        {formError ? (
          <Alert variant="error" title="Eingabe" className="mb-4">
            {formError}
          </Alert>
        ) : null}
        <div className="grid gap-4">
          <TextInput
            label="Bestellnummer"
            value={form.bestellnummer}
            onChange={(e) => setForm((f) => ({ ...f, bestellnummer: e.target.value }))}
            placeholder="z. B. PO-2025-00123"
          />
          <SelectInput
            label="Ansprechpartner (optional)"
            value={form.ansprechpartnerId}
            onChange={(e) => setForm((f) => ({ ...f, ansprechpartnerId: e.target.value }))}
          >
            <option value="">— Kein Ansprechpartner —</option>
            {ansprechpartner.map((ap) => (
              <option key={ap.id} value={ap.id}>
                {ansprechpartnerDisplayName(ap)}
              </option>
            ))}
          </SelectInput>
          <SelectInput
            label="Objektadresse (optional)"
            value={form.heroAddressId}
            onChange={(e) => setForm((f) => ({ ...f, heroAddressId: e.target.value }))}
          >
            <option value="">— Keine Objektadresse —</option>
            {objektadressen.map((addr) => (
              <option
                key={addr.ort_id != null ? `ort-${addr.ort_id}` : `addr-${addr.address_id}`}
                value={addr.address_id}
              >
                {addr.title || addr.street || `Adresse #${addr.address_id}`}
              </option>
            ))}
          </SelectInput>
        </div>
      </Dialog>
    </Card>
  );
}
